import type { ModuleId } from "./definitions.js";
import { relocatePatrolGameplayPoint } from "./contract-geography.js";
import { TRAINING } from "./training.js";

export const TUTORIAL_MILESTONES = ["moved", "targeted", "fired", "defended", "helped"] as const;
export const TRAINING_MILESTONES = [
  "moved", "stationVisited", "contractAccepted", "targeted", "fired", "defended", "helped",
  "cargoCollected", "contractDelivered", "moduleEquipped", "moduleUsed",
] as const;
export type TutorialMilestone = (typeof TRAINING_MILESTONES)[number];
export const TRAINING_CONTRACT_GOAL = TRAINING_MILESTONES.indexOf("cargoCollected") + 1;
export const TUTORIAL_MOVEMENT_DISTANCE = 45;
export const TUTORIAL_ACTIVATION_DISTANCE = 1000;
export const TUTORIAL_AGGRO_DISTANCE = 750;
export const INTRO_ENEMY_ID = "intro-raider";
export const INTRO_TRANSPORT_ID = "intro-transport";
export interface TutorialState {
  route?: "training";
  version: 1 | 2;
  milestones: TutorialMilestone[];
  rewardGranted: boolean;
  docked: boolean;
}
export interface LegacyTutorialState extends TutorialState {
  version: 1;
  milestones: (typeof TUTORIAL_MILESTONES)[number][];
}
export interface TrainingTutorialState extends TutorialState {
  version: 2;
  route: "training";
}
export function tutorialLocation(state: TutorialState | null | undefined) {
  return state?.route === "training"
    ? TRAINING
    : { ...TUTORIAL, enemyId: INTRO_ENEMY_ID, transportId: INTRO_TRANSPORT_ID };
}
export const TUTORIAL: {
  spawn: { x: number; y: number };
  transport: { x: number; y: number };
  enemy: { x: number; y: number };
  firstModule: ModuleId;
} = {
  spawn: { x: 460, y: 1100 },
  transport: relocatePatrolGameplayPoint({ x: 1260, y: 1820 }),
  enemy: relocatePatrolGameplayPoint({ x: 1470, y: 1730 }),
  firstModule: "volley",
};
export function createTutorialState(): LegacyTutorialState {
  return { version: 1, milestones: [], rewardGranted: false, docked: false };
}
export function createTrainingTutorialState(): TrainingTutorialState {
  return { version: 2, route: "training", milestones: [], rewardGranted: false, docked: false };
}
export function tutorialMilestones(state: TutorialState): readonly TutorialMilestone[] {
  return state.version === 2 ? TRAINING_MILESTONES : TUTORIAL_MILESTONES;
}
export function tutorialCount(state: TutorialState | null): number {
  return state ? tutorialMilestones(state).filter((milestone) => state.milestones.includes(milestone)).length : 0;
}
export function tutorialStage(state: TutorialState): TutorialMilestone | "return" | "complete" {
  return (
    tutorialMilestones(state).find((milestone) => !state.milestones.includes(milestone)) ??
    (state.docked && state.rewardGranted ? "complete" : "return")
  );
}
export function tutorialMissionComplete(state: TutorialState): boolean {
  return state.version === 2 ? state.milestones.includes("cargoCollected")
    : tutorialCount(state) === TUTORIAL_MILESTONES.length;
}
export function canLeaveTraining(state: TutorialState): boolean {
  return state.route === "training" && !state.docked && state.rewardGranted && tutorialStage(state) === "return";
}
